'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSelectedLayoutSegment } from 'next/navigation';
import {
  useMenuActions,
  useMenuState,
} from '@noirproperties/react/menu-provider/menu-hooks';
import { cn } from '@/lib/cn';
import { useUser } from '@/providers/user-hooks';
import type { NavigationItems } from '@/menus/main-menu';

type DesktopSidebarProps = {
  navigation: NavigationItems;
};

export function DesktopSidebar({ navigation }: DesktopSidebarProps) {
  const user = useUser();
  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
      <div className="flex h-32 shrink-0 items-center">
        {/* Desktop Logo */}
        <Link className="p-2" href="/">
          <span className="sr-only">Noir Properties</span>
          <Image
            alt="Noir Properties"
            className="h-16 w-auto"
            height={96}
            priority
            src="/noirproperties.svg"
            width={96}
          />
        </Link>
      </div>
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul className="-mx-2 space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <NavigationItemLink item={item} kind="desktop" />
                </li>
              ))}
            </ul>
          </li>
          {/* TODO: Do we need TEAMS? Good for different agency branches maybe? */}
          {/* <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              Your teams
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              {teams.map((team) => (
                <li key={team.name}>
                  <a
                    className={cn(
                      team.current
                        ? 'bg-gray-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                    )}
                    href={team.href}
                  >
                    <span
                      className={cn(
                        team.current
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600',
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',
                      )}
                    >
                      {team.initial}
                    </span>
                    <span className="truncate">{team.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </li> */}
          <li className="-mx-6 mt-auto">
            <a
              className="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-50"
              href="#"
            >
              {/* TODO: Dynamic image */}
              <Image
                alt=""
                className="h-8 w-8 rounded-full bg-gray-50"
                height={32}
                src={user.picture}
                width={32}
              />
              <span className="sr-only">Your profile</span>
              {/* TODO: User name */}
              <span aria-hidden="true">{user.name}</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  );
}

type MobileSidebarProps = {
  navigation: NavigationItems;
};

function MobileSidebar({ navigation }: MobileSidebarProps) {
  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
      <div className="flex h-32 shrink-0 items-center">
        <Link className="p-2" href="/">
          <span className="sr-only">Noir Properties</span>
          <Image
            alt="Noir Properties Logo"
            className="h-16 w-auto"
            height={96}
            priority
            src="/noirproperties.svg"
            width={96}
          />
        </Link>
      </div>
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul className="-mx-2 space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <NavigationItemLink item={item} kind="mobile" />
                </li>
              ))}
            </ul>
          </li>
          {/* TODO: Do we need TEAMS? Good for different agency branches maybe? */}
          {/* <li>

            <div className="text-xs font-semibold leading-6 text-gray-400">
              Your teams
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              {teams.map((team) => (
                <li key={team.name}>
                  <a
                    className={cn(
                      team.current
                        ? 'bg-gray-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                    )}
                    href={team.href}
                  >
                    <span
                      className={cn(
                        team.current
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600',
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',
                      )}
                    >
                      {team.initial}
                    </span>
                    <span className="truncate">{team.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </li> */}
        </ul>
      </nav>
    </div>
  );
}

type NavigationItemLinkProps = {
  item: NavigationItems[number];
  // TODO: Rename kind to something else?
  kind: 'desktop' | 'mobile';
};

function NavigationItemLink({ item, kind }: NavigationItemLinkProps) {
  const segment = useSelectedLayoutSegment();

  const isActive = item.activeSegment === segment;

  if (kind === 'desktop') {
    return (
      <Link
        className={cn(
          isActive
            ? 'bg-gray-50 text-indigo-600'
            : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
          'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
        )}
        href={item.href}
      >
        <item.icon
          aria-hidden="true"
          className={cn(
            isActive
              ? 'text-indigo-600'
              : 'text-gray-400 group-hover:text-indigo-600',
            'h-6 w-6 shrink-0',
          )}
        />
        {item.name}
      </Link>
    );
  }

  return (
    <Link
      className={cn(
        isActive
          ? 'bg-gray-50 text-indigo-600'
          : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
        'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
      )}
      href={item.href}
    >
      <item.icon
        aria-hidden="true"
        className={cn(
          isActive
            ? 'text-indigo-600'
            : 'text-gray-400 group-hover:text-indigo-600',
          'h-6 w-6 shrink-0',
        )}
      />
      {item.name}
    </Link>
  );
}

type MobileMenuProps = {
  navigation: NavigationItems;
};

export function MobileMenu({ navigation }: MobileMenuProps) {
  const { isOpen } = useMenuState();
  const { toggleMenu } = useMenuActions();

  return (
    <Transition.Root as={React.Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-50 lg:hidden" onClose={toggleMenu}>
        <Transition.Child
          as={React.Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={React.Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <Transition.Child
                as={React.Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    className="-m-2.5 p-2.5"
                    onClick={toggleMenu}
                    type="button"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="h-6 w-6 text-white"
                    />
                  </button>
                </div>
              </Transition.Child>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <MobileSidebar navigation={navigation} />
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
